/**
 * Fermentables — malts, adjunct grains, sugars, extracts and fruit.
 *
 * Units follow the `@beerwright/core` canon (PLAN §5): percentages are 0–100,
 * colour is carried as both °L and EBC, temperatures are °C. The mapping onto
 * core's `FermentableAddition` still lands separately; the one thing taken
 * from core directly is {@link SUCROSE_PPG}, a physical constant rather than a
 * piece of arithmetic, and its own doc says why.
 */

import { MAX_SUCROSE_PPG } from '@beerwright/core/defaults';
import { z } from 'zod';
import {
  descriptionSchema,
  originSchema,
  productUrlSchema,
  provenanceSchema,
  slugSchema,
  tagsSchema,
} from './common';

// ---------------------------------------------------------------------------
// Enumerations
// ---------------------------------------------------------------------------

/**
 * What the fermentable *is*, for grouping and for picking sane editor
 * defaults (a `sugar` needs no mashing, a `roasted` malt is capped low).
 */
export const fermentableTypeSchema = z.enum([
  /** Fully modified, high-DP malt intended to make up the bulk of a grist. */
  'base',
  /** Stewed then kilned; crystallised sugars, no diastatic power. */
  'crystal-caramel',
  /** Roasted grain — chocolate, black, roasted barley, carafa. */
  'roasted',
  /** Kilned or toasted colour/flavour malts — Munich, Vienna, biscuit, amber. */
  'kilned-toasted',
  /** Unmalted or flaked grain and other grain adjuncts. */
  'adjunct-grain',
  /** Simple and complex sugars, syrups and honey. */
  'sugar',
  /** Malt extract, dry or liquid. */
  'extract',
  /** Fruit and fruit purée/juice. */
  'fruit',
]);

/** @see fermentableTypeSchema */
export type FermentableType = z.infer<typeof fermentableTypeSchema>;

/**
 * The class the mash-pH model buckets this grain into (PLAN §5.4).
 *
 * Kept as a string enum on purpose: the catalog states the class, and
 * `@beerwright/core` owns the DI-pH / buffering coefficients it maps each class
 * onto. Entries that carry maltster-published titration data override the
 * class defaults via {@link fermentableSchema}'s `diPh`/`bufferingCapacity`.
 */
export const maltClassSchema = z.enum([
  /** Continental pilsner malt — the least acidic, highest DI pH. */
  'base-pilsner',
  /** Pale ale / 2-row / Maris Otter and friends. */
  'base-pale',
  /** Munich and Vienna: more colour, slightly lower DI pH. */
  'munich-vienna',
  /** Malted wheat, rye and their kilned variants. */
  'wheat',
  /** Crystal/caramel malts; acidity scales with colour. */
  'crystal',
  /** Roasted malts and roasted barley; strongly acidic. */
  'roasted',
  /** Sauermalz / acid malt, ~2–4 % lactic acid by weight. */
  'acidulated',
  /** Contributes no measurable buffering: sugars, extracts, flaked adjuncts. */
  'non-buffering',
]);

/** @see maltClassSchema */
export type MaltClass = z.infer<typeof maltClassSchema>;

// ---------------------------------------------------------------------------
// Entry
// ---------------------------------------------------------------------------

/**
 * One purchasable fermentable product (or one clearly-defined generic).
 *
 * @remarks
 * PPG is deliberately **not** stored — it is derived from
 * `extractFineDryPercent` and `moisturePercent` by {@link fermentablePpg} so
 * the two can never disagree.
 */
export const fermentableSchema = z.strictObject({
  /** Stable slug including the maker, e.g. `weyermann-pilsner`. */
  id: slugSchema,
  /** Product name as the maker sells it, without the maker prefix. */
  name: z.string().min(2).max(80),
  /** Maltster, sugar producer or `Generic` for class entries. */
  maker: z.string().min(2).max(60),
  /** Country of origin, as displayed. */
  origin: originSchema,
  /** @see fermentableTypeSchema */
  type: fermentableTypeSchema,
  /** @see maltClassSchema */
  maltClass: maltClassSchema,
  /**
   * Fine-grind extract, dry basis, as % of dry weight.
   *
   * The maltster's headline yield figure. Sugars are 100 %; extracts are the
   * solids fraction expressed the same way so the yield maths is uniform.
   */
  extractFineDryPercent: z.number().min(0).max(100),
  /** Moisture as shipped, % of total weight. Sugars/syrups carry their own. */
  moisturePercent: z.number().min(0).max(60),
  /** Colour in degrees Lovibond. */
  colorLovibond: z.number().min(0).max(700),
  /** Colour in EBC. Must agree with °L via `EBC ≈ 2.65·°L − 1.2`. */
  colorEbc: z.number().min(0).max(1900),
  /** Diastatic power in °Lintner. 0 for anything that cannot convert itself; distilling malts reach ~260. */
  diastaticPowerLintner: z.number().min(0).max(300),
  /** Suggested ceiling as % of the grist. Guidance for the editor, not a law. */
  suggestedMaxPercent: z.number().min(0.5).max(100),
  /** True when the grain must be mashed to convert; false for steep/boil-only. */
  needsMashing: z.boolean(),
  /**
   * Distilled-water pH of a test mash, where the maltster publishes it.
   *
   * `null` means "use the {@link maltClassSchema} default". We never guess a
   * number here — a wrong DI pH silently poisons the whole pH model.
   */
  diPh: z.number().min(3).max(7).nullable(),
  /**
   * Buffering capacity in mEq per kg per pH unit, where published.
   *
   * `null` means "use the class default". Positive values buffer against
   * acidification; the sign convention matches Troester's titration data.
   */
  bufferingCapacity: z.number().min(-800).max(800).nullable(),
  /** Our own words. Never the maltster's marketing copy. */
  description: descriptionSchema,
  /** Search/filter tags. */
  tags: tagsSchema,
  /** @see provenanceSchema */
  /** @see productUrlSchema */
  url: productUrlSchema.optional(),
  provenance: provenanceSchema,
});

/** @see fermentableSchema */
export type Fermentable = z.infer<typeof fermentableSchema>;

// ---------------------------------------------------------------------------
// Derived values
// ---------------------------------------------------------------------------

/**
 * Points per pound per gallon of pure sucrose — the yield ceiling every
 * extract figure is expressed against.
 *
 * Core's number, not a second copy of it. This package states facts and core
 * owns the maths (see the package README), but a physical constant is a fact
 * that both of them have to agree on to the last decimal: the catalog derives
 * a PPG for the ingredient library, core derives one for the recipe, and
 * interchange converts a file's `YIELD` through this one. While the catalog
 * carried its own 46.21 the three disagreed in the fourth decimal, which is
 * nothing on a gravity and everything on a fingerprint — an imported override
 * could be identified as import-derived precisely because it matched
 * `FGDB × 46.21` to six decimals. So the constant is imported rather than
 * restated, and the alias is kept because `SUCROSE_PPG` is the name this
 * package's own arithmetic reads in.
 */
export const SUCROSE_PPG = MAX_SUCROSE_PPG;

/**
 * As-is (coarse-grind-agnostic) PPG derived from the published dry-basis
 * extract and the shipped moisture.
 *
 * ```
 * PPG = extractFineDryPercent/100 × (1 − moisturePercent/100) × 46.214
 * ```
 *
 * Fine-grind is used as published; the brewhouse's coarse/fine difference and
 * mash efficiency are the *recipe's* concern, not the catalog's.
 *
 * @param f - The fermentable to derive from.
 * @returns Gravity points per pound per US gallon at 100 % efficiency.
 */
export function fermentablePpg(f: Pick<Fermentable, 'extractFineDryPercent' | 'moisturePercent'>) {
  return (f.extractFineDryPercent / 100) * (1 - f.moisturePercent / 100) * SUCROSE_PPG;
}

/** As-is yield as a percentage of sucrose — the other common way to quote it. */
export function fermentableYieldPercent(
  f: Pick<Fermentable, 'extractFineDryPercent' | 'moisturePercent'>,
) {
  return (fermentablePpg(f) / SUCROSE_PPG) * 100;
}

/** EBC implied by a Lovibond figure: `EBC = 2.65·°L − 1.2` (clamped at 0). */
export function lovibondToEbc(lovibond: number): number {
  return Math.max(0, 2.65 * lovibond - 1.2);
}

/**
 * The inverse of {@link lovibondToEbc}, for a brewer who reads EBC.
 *
 * Colour is stored in °L everywhere in the app, so an EBC figure typed into a
 * form has to come back through the same relation it would be shown by —
 * anything else and a number typed, saved and reopened would not be the number
 * that was typed. Clamped at 0 like its forward: EBC below 1.2 implies a
 * negative °L, which no malt has.
 */
export function ebcToLovibond(ebc: number): number {
  return Math.max(0, (ebc + 1.2) / 2.65);
}
